import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './CustomerApplicationForm.css';
import IdentityForm from '../FormComponents/IdentityForm';
import ContactForm from '../FormComponents/ContactForm';
import WorkForm from '../FormComponents/WorkForm';
import VehicleForm from '../FormComponents/VehicleForm';
import ConfirmationForm from '../FormComponents/ConfirmationForm';
import SuccessPage from '../FormComponents/SuccessPage';
import { validateTCNumber } from '../utils/validators';

const ApplicationPage = () => {
    // URL'den gelen verileri al
    const location = useLocation();
    const navigate = useNavigate();

    // State'ten verileri al, yoksa varsayılan değerler kullan
    const { vehicleAmount = 150000, monthlyPayment = 5000, term = 36 } = location.state || {};

    const [activeTab, setActiveTab] = useState('kimlik'); // kimlik, iletisim, calisma, arac, notlar
    const [progress, setProgress] = useState(20);

    const [formData, setFormData] = useState({
        // Kimlik Bilgileri
        name: '',
        surname: '',
        tcNumber: '',
        birthDate: '',
        gender: '',
        education: '',

        // İletişim Bilgileri
        phone: '',
        email: '',
        address: '',
        city: '',
        district: '',

        // İş ve Gelir Bilgileri
        occupation: '',
        workplaceName: '',
        employmentType: '',
        employmentDuration: '',
        income: '',
        workAddress: '',
        workPhone: '',

        // Araç Tercihi
        preferredVehicleBrand: '',
        preferredVehicleModel: '',
        vehicleYear: '',
        downPayment: '',

        // Notlar ve Onaylar
        notes: '',
        hasAcceptedTerms: false,
        hasAcceptedCommunication: false
    });

    const [validated, setValidated] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [tcError, setTcError] = useState('');

    // Form değişikliklerini izle
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let processedValue = value;

        // Text alanları için büyük harf dönüşümü
        const textFields = [
            'name', 'surname', 'address', 'city', 'district',
            'occupation', 'workplaceName', 'workAddress',
            'preferredVehicleBrand', 'preferredVehicleModel'
        ];

        if (textFields.includes(name)) {
            processedValue = value.toUpperCase();
        }

        // TC Kimlik için özel kontroller
        if (name === 'tcNumber') {
            // Sadece sayı girişine izin ver
            if (value && !/^[0-9]+$/.test(value)) {
                return;
            }

            // TC Kimlik doğrulama hatasını sıfırla
            setTcError('');

            // TC Kimlik uzunluğu 11 haneden fazla olmamalı
            if (value.length > 11) {
                return;
            }

            // 11 haneye ulaştığında doğrulama yap
            if (value.length === 11) {
                if (!validateTCNumber(value)) {
                    setTcError('Geçersiz T.C. Kimlik Numarası');
                }
            }
        }

        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : processedValue
        });
    };

    // Sonraki sekmeye geç
    const handleNext = (currentTab) => {
        const tabsOrder = ['kimlik', 'iletisim', 'calisma', 'arac', 'notlar'];
        const currentIndex = tabsOrder.indexOf(currentTab);

        if (currentIndex < tabsOrder.length - 1) {
            // Form validasyonu
            let isValid = true;

            // TC Kimlik doğrulama
            if (currentTab === 'kimlik' && formData.tcNumber && !validateTCNumber(formData.tcNumber)) {
                setTcError('Geçersiz T.C. Kimlik Numarası');
                isValid = false;
            }

            // Tab içeriğinin durumuna göre boş alanları kontrol et
            if (currentTab === 'kimlik') {
                if (!formData.name || !formData.surname || !formData.tcNumber || !formData.birthDate || !formData.education) {
                    isValid = false;
                }
            } else if (currentTab === 'iletisim') {
                if (!formData.phone || !formData.email || !formData.address || !formData.city || !formData.district) {
                    isValid = false;
                }
            } else if (currentTab === 'calisma') {
                if (!formData.occupation || !formData.employmentType || !formData.income) {
                    isValid = false;
                }
            }

            if (!isValid) {
                setValidated(true);
                return;
            }

            const nextTab = tabsOrder[currentIndex + 1];
            setActiveTab(nextTab);
            setProgress((currentIndex + 2) * 20);
            setValidated(false);
        }
    };

    // Önceki sekmeye dön
    const handlePrev = (currentTab) => {
        const tabsOrder = ['kimlik', 'iletisim', 'calisma', 'arac', 'notlar'];
        const currentIndex = tabsOrder.indexOf(currentTab);

        if (currentIndex > 0) {
            const prevTab = tabsOrder[currentIndex - 1];
            setActiveTab(prevTab);
            setProgress((currentIndex) * 20);
            setValidated(false);
        }
    };

    // Form gönderimi
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Form validasyonu
        if (!formData.hasAcceptedTerms) {
            setError('Lütfen kullanım koşullarını ve aydınlatma metnini kabul ediniz.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            // Başvuru verilerini hazırla
            const applicationData = {
                ...formData,
                vehicleAmount,
                monthlyPayment,
                term,
                applicationDate: new Date().toISOString()
            };

            // Backend API'ye başvuru verilerini gönder
            const response = await axios.post('/api/applications/submit', applicationData);

            if (response.data && response.data.success) {
                // Başarılı başvuru durumunda
                console.log('Başvuru başarıyla gönderildi:', response.data);
                setIsSubmitted(true);
            } else {
                // API başarılı yanıt vermedi
                setError(response.data?.message || 'Başvuru gönderilirken bir hata oluştu');
            }
        } catch (error) {
            console.error('Başvuru hatası:', error);
            setError('Başvuru işlenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Ana sayfaya dön
    const handleGoBack = () => {
        navigate('/');
    };

    // Başarılı başvuru sayfası
    if (isSubmitted) {
        return (
            <SuccessPage
                formData={formData}
                vehicleAmount={vehicleAmount}
                monthlyPayment={monthlyPayment}
                term={term}
                handleGoBack={handleGoBack}
            />
        );
    }

    return (
        <Container className="py-4">
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                    {/* Başlık ve Geri Butonu - Üst başlık kaldırıldı */}
                    <div className="d-flex justify-content-end align-items-center mb-2">
                        <Button variant="outline-secondary" onClick={handleGoBack} className="d-flex align-items-center">
                            <FaArrowLeft className="me-2" /> Geri Dön
                        </Button>
                    </div>

                    {/* Ana başlık sadece bu kısımda yer alacak */}
                    <div className="text-center mb-4">
                        <h3 className="text-secondary fw-bold">SENETLİ, TAKSİTLE ARAÇ FİNANSMANI BAŞVURUSU</h3>
                    </div>

                    {/* İlerleme Çubuğu */}
                    <div className="progress-container mb-4">
                        <div className="progress">
                            <div
                                className="progress-bar"
                                role="progressbar"
                                style={{ width: `${progress}%` }}
                                aria-valuenow={progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>
                        </div>
                        <div className="step-indicators d-flex justify-content-between">
                            <div className={`step-circle ${activeTab === 'kimlik' || progress >= 20 ? 'active' : ''}`}>1</div>
                            <div className={`step-circle ${activeTab === 'iletisim' || progress >= 40 ? 'active' : ''}`}>2</div>
                            <div className={`step-circle ${activeTab === 'calisma' || progress >= 60 ? 'active' : ''}`}>3</div>
                            <div className={`step-circle ${activeTab === 'arac' || progress >= 80 ? 'active' : ''}`}>4</div>
                            <div className={`step-circle ${activeTab === 'notlar' ? 'active' : ''}`}>5</div>
                        </div>
                        {/* Adım etiketleri daha büyük ve okunabilir */}
                        <div className="step-labels d-flex justify-content-between text-center mt-2">
                            <div className="step-label">
                                <span className={`step-text ${activeTab === 'kimlik' ? 'active-step' : ''}`}>Kimlik Bilgileri</span>
                            </div>
                            <div className="step-label">
                                <span className={`step-text ${activeTab === 'iletisim' ? 'active-step' : ''}`}>İletişim Bilgileri</span>
                            </div>
                            <div className="step-label">
                                <span className={`step-text ${activeTab === 'calisma' ? 'active-step' : ''}`}>İş Bilgileri</span>
                            </div>
                            <div className="step-label">
                                <span className={`step-text ${activeTab === 'arac' ? 'active-step' : ''}`}>Araç Bilgileri</span>
                            </div>
                            <div className="step-label">
                                <span className={`step-text ${activeTab === 'notlar' ? 'active-step' : ''}`}>Onay</span>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" className="mb-4">
                            {error}
                        </Alert>
                    )}

                    <div className="application-summary mb-4 p-3 bg-light rounded">
                        <Row className="mb-2">
                            <Col md={4} className="text-md-end text-start fw-bold">Araç Bedeli:</Col>
                            <Col md={8}>₺ {vehicleAmount.toLocaleString('tr-TR')}</Col>
                        </Row>
                        <Row className="mb-2">
                            <Col md={4} className="text-md-end text-start fw-bold">Aylık Ödeme:</Col>
                            <Col md={8}>₺ {monthlyPayment.toLocaleString('tr-TR')}</Col>
                        </Row>
                        <Row>
                            <Col md={4} className="text-md-end text-start fw-bold">Vade:</Col>
                            <Col md={8}>{term} Ay</Col>
                        </Row>
                    </div>

                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        {/* Kimlik Bilgileri */}
                        <IdentityForm
                            isActive={activeTab === 'kimlik'}
                            formData={formData}
                            tcError={tcError}
                            handleChange={handleChange}
                            handleNext={() => handleNext('kimlik')}
                        />

                        {/* İletişim Bilgileri */}
                        <ContactForm
                            isActive={activeTab === 'iletisim'}
                            formData={formData}
                            handleChange={handleChange}
                            handlePrev={() => handlePrev('iletisim')}
                            handleNext={() => handleNext('iletisim')}
                        />

                        {/* İş Bilgileri */}
                        <WorkForm
                            isActive={activeTab === 'calisma'}
                            formData={formData}
                            handleChange={handleChange}
                            handlePrev={() => handlePrev('calisma')}
                            handleNext={() => handleNext('calisma')}
                        />

                        {/* Araç Bilgileri */}
                        <VehicleForm
                            isActive={activeTab === 'arac'}
                            formData={formData}
                            handleChange={handleChange}
                            handlePrev={() => handlePrev('arac')}
                            handleNext={() => handleNext('arac')}
                        />

                        {/* Onay ve Notlar */}
                        <ConfirmationForm
                            isActive={activeTab === 'notlar'}
                            formData={formData}
                            handleChange={handleChange}
                            handlePrev={() => handlePrev('notlar')}
                            isSubmitting={isSubmitting}
                            vehicleAmount={vehicleAmount}
                            monthlyPayment={monthlyPayment}
                            term={term}
                        />
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default ApplicationPage;